import { useMemo } from 'react';
import { BuildingSummary, CoordinatesModel } from '../../types/protocols';
import { Userprofile } from '../../Helpers/UsersContext';
import { buildRoutes, daysWithLogs, PersonRoute } from '../../Pages/Analysis/utils/routes';
import { BuildingServicesData } from './useYearComparison';

interface UseRoutesArgs {
    servicesData: BuildingServicesData | null;
    buildings: BuildingSummary[];
    users: Userprofile[];
    /** The company (user group) whose people are shown */
    groupKey: string | null;
    /** The picked day; null = the company's latest day with logs */
    day: string | null;
}

export interface PlacedBuilding {
    name: string;
    coordinates: CoordinatesModel;
}

export interface Routes {
    /** Buildings with coordinates; the others are left out of the routes */
    placedBuildings: Map<string, PlacedBuilding>;
    /** The company's days with logs, ascending */
    days: string[];
    /** The shown day, null when the company has no logs at all */
    day: string | null;
    routes: PersonRoute[];
}

/**
 * Derives the Resvägar routes from the already fetched service logs. A log
 * belongs to the company when its user is a member of the group today.
 * Read-only: no fetch of its own.
 */
export const useRoutes = ({ servicesData, buildings, users, groupKey, day }: UseRoutesArgs): Routes => {
    const placedBuildings = useMemo(() => {
        const placed = new Map<string, PlacedBuilding>();
        buildings.forEach(building => {
            const { latitude, longitude } = building.coordinates ?? {};
            if (typeof latitude === 'number' && typeof longitude === 'number') {
                placed.set(building.key, { name: building.name || building.key, coordinates: { latitude, longitude } });
            }
        });
        return placed;
    }, [buildings]);

    const placedKeys = useMemo(() => new Set(placedBuildings.keys()), [placedBuildings]);

    const memberUids = useMemo(
        () => new Set(groupKey ? users.filter(user => user.groups?.includes(groupKey)).map(user => user.key) : []),
        [users, groupKey],
    );

    const logs = servicesData?.servicesLogs;
    const days = useMemo(() => daysWithLogs(logs, memberUids, placedKeys), [logs, memberUids, placedKeys]);
    const shownDay = day ?? days[days.length - 1] ?? null;

    const routes = useMemo(
        () => (shownDay ? buildRoutes(logs, shownDay, memberUids, placedKeys) : []),
        [logs, shownDay, memberUids, placedKeys],
    );

    return { placedBuildings, days, day: shownDay, routes };
};
